import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { resolveBackButtonHandler } from '../android/androidBackStack';
import { useAndroidBackClose } from '../android/useAndroidBackClose';
import { canReceiveReturnedFocus, keepTabInside } from './dialogFocus';
import { Icon } from './ui/Icon';

interface ModalProps {
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  isBusy?: boolean;
  size?: 'default' | 'wide';
  /**
   * Rediseño · Bloque C. `n3` dibuja el mismo diálogo con la piel del rediseño (clases `.n3-dialog`,
   * en `primitives.css`): hoja al pie en teléfono, tarjeta centrada en una ventana ancha. Solo cambia
   * el marcado: el foco, Escape, el Atrás de Android y `isBusy` son exactamente los mismos. Se usa a
   * través de `Dialog`.
   *
   * P-075: sin la prop, los diálogos de las pantallas operativas también llevan el marco del rediseño,
   * con el ancho de un formulario (`n3-dialog--form`) o el de un detalle con tablas (`size="wide"`). La
   * piel anterior queda solo para quien la pida con `skin="legacy"`.
   */
  skin?: 'legacy' | 'n3';
  onClose: () => void;
}

export function Modal({
  title,
  description,
  children,
  footer,
  isBusy = false,
  size = 'default',
  skin,
  onClose,
}: ModalProps) {
  // Antes el título llevaba un id fijo: con un diálogo abierto encima de otro (una confirmación
  // sobre un formulario) había dos elementos con el mismo id y los dos diálogos se llamaban igual.
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLElement>(null);

  // Quién tenía el foco ANTES de abrir, para devolvérselo al cerrar. Se lee durante el primer
  // render y no en un efecto: un hijo con `autoFocus` toma el foco al montarse, antes de cualquier
  // efecto, y entonces lo «anterior» ya sería algo de dentro del propio diálogo.
  const [returnFocusTo] = useState<Element | null>(() => (typeof document === 'undefined' ? null : document.activeElement));

  // Fix HIGH-1: el Back físico respeta la misma regla que Escape, el fondo y la «×» — mientras
  // `isBusy` queda consumido sin cerrar. `resolveBackButtonHandler` usa la MISMA prop `isBusy`
  // (única fuente de verdad). El registro en la pila es estable, y un diálogo queda siempre por
  // debajo de los que contiene: ver `useAndroidBackClose`.
  const isTopmost = useAndroidBackClose(resolveBackButtonHandler(isBusy, onClose), true, dialogRef);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Solo responde el diálogo de más arriba: con uno abierto sobre otro, los dos escuchan.
      if (!isTopmost()) return;
      if (event.key === 'Escape' && !isBusy) onClose();
      if (event.key === 'Tab' && dialogRef.current && keepTabInside(dialogRef.current, event.shiftKey)) event.preventDefault();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isBusy, isTopmost, onClose]);

  // Al abrir, el foco entra al diálogo; al cerrar, vuelve a quien lo abrió.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return undefined;
    // Si algo de dentro ya lo tomó (`autoFocus`), se respeta. Si no, lo recibe el contenedor y no el
    // primer campo: enfocar un campo levantaría el teclado en pantalla nada más abrir.
    if (!dialog.contains(document.activeElement)) dialog.focus({ preventScroll: true });

    return () => {
      // Con el diálogo todavía en pantalla esto no es un cierre (StrictMode repite los efectos en
      // desarrollo): no se toca el foco.
      if (dialog.isConnected) return;
      // Solo se devuelve si el foco quedó huérfano; si la app ya lo puso en otro sitio, se respeta.
      const active = document.activeElement;
      const orphaned = active === null || active === document.body || dialog.contains(active);
      if (orphaned && canReceiveReturnedFocus(returnFocusTo)) returnFocusTo.focus({ preventScroll: true });
    };
  }, [returnFocusTo]);

  // Un control apagado no emite eventos de teclado. Si el diálogo pasa a ocupado con el foco en el
  // botón que se acaba de apagar («Confirmar»), el foco sube al contenedor para que Tab siga dentro.
  useEffect(() => {
    const dialog = dialogRef.current;
    const active = document.activeElement;
    if (!isBusy || !dialog || !(active instanceof HTMLElement) || !dialog.contains(active)) return;
    if ((active as HTMLButtonElement).disabled) dialog.focus({ preventScroll: true });
  }, [isBusy]);

  if (skin !== 'legacy') {
    // El ancho: el del diálogo del rediseño (`Dialog`), el de un formulario de captura (sin `skin`) o el
    // de un detalle con tablas (`size="wide"`).
    const width = size === 'wide' ? ' n3-dialog--wide' : skin === 'n3' ? '' : ' n3-dialog--form';
    return (
      <div className="n3-dialog-backdrop" role="presentation" onMouseDown={() => !isBusy && onClose()}>
        <section
          aria-describedby={description ? descriptionId : undefined}
          aria-labelledby={titleId}
          aria-modal="true"
          className={`n3-dialog${width}`}
          onMouseDown={(event) => event.stopPropagation()}
          ref={dialogRef}
          role="dialog"
          tabIndex={-1}
        >
          <span aria-hidden="true" className="n3-dialog__grab" />
          <header className="n3-dialog__header">
            <div className="n3-dialog__heading">
              <h2 className="n3-dialog__title" id={titleId}>{title}</h2>
              {description && <p className="n3-dialog__description" id={descriptionId}>{description}</p>}
            </div>
            <button aria-label="Cerrar" className="n3-icon-button n3-icon-button--soft n3-dialog__close" disabled={isBusy} onClick={onClose} type="button">
              <Icon name="close" size={18} />
            </button>
          </header>
          <div className="n3-dialog__body">{children}</div>
          {footer && <footer className="n3-dialog__footer">{footer}</footer>}
        </section>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={() => !isBusy && onClose()}>
      <section
        aria-describedby={description ? descriptionId : undefined}
        aria-labelledby={titleId}
        aria-modal="true"
        className={`modal-card${size === 'wide' ? ' modal-card--wide' : ''}`}
        onMouseDown={(event) => event.stopPropagation()}
        ref={dialogRef}
        role="dialog"
        tabIndex={-1}
      >
        <header className="modal-card__header">
          <div>
            <h2 id={titleId}>{title}</h2>
            {description && <p id={descriptionId}>{description}</p>}
          </div>
          <button aria-label="Cerrar" className="icon-button" disabled={isBusy} onClick={onClose} type="button">×</button>
        </header>
        <div className="modal-card__body">{children}</div>
        {footer && <footer className="modal-card__footer">{footer}</footer>}
      </section>
    </div>
  );
}
